import { Component, computed, inject, input, linkedSignal, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import {
	AIChatConversatieStatus,
	getAIChatConversatieStatusName,
	RAIChatBehandeling,
	RAIChatConversatie,
	RAIChatOpmerking,
} from '@cumlaude/service-contract';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { RestService } from '@cumlaude/shared-services';
import { ScrollService } from '../../services/scroll.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, map, Observable, shareReplay, switchMap } from 'rxjs';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { ChatOpmerkingenComponent, ChatTurnsComponent, DashboardURLComponents } from '@cumlaude/shared-components-features';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import { ConfirmDialogComponent } from '@cumlaude/shared-components-dialogs';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { Option, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { CompactNumberPipe } from '@cumlaude/shared-pipes';
import { SENTIMENT_ICON } from './sentiment-icon';
import { AIChatOpmerkingDialogComponent, OpmerkingDialogData } from './ai-chat-opmerking-dialog.component';
import { AIChatsZoekService } from './ai-chats-zoek.service';

@Component({
	selector: 'app-ai-chat-page',
	templateUrl: './ai-chat-page.component.html',
	styleUrls: ['./ai-chat-page.component.scss'],
	imports: [
		ButtonComponent,
		ChatOpmerkingenComponent,
		ChatTurnsComponent,
		CompactNumberPipe,
		ConfirmDialogComponent,
		DatePipe,
		AsyncPipe,
		IconComponent,
		SingleSelectDropdownComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AIChatPageComponent {
	protected readonly SENTIMENT_ICON = SENTIMENT_ICON;
	private readonly router = inject(Router);
	private readonly restService = inject(RestService);
	private readonly scrollService = inject(ScrollService);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly dialog = inject(Dialog);
	private readonly toastr = inject(ToastrService);
	private readonly zoek = inject(AIChatsZoekService);

	@ViewChild('confirmOpmerkingVerwijderen')
	confirmOpmerkingVerwijderen!: TemplateRef<unknown>;

	id = input<number | undefined>(undefined);

	private readonly id$ = toObservable(this.id);

	/**
	 * Vorige en volgende conversatie in het laatste zoekresultaat van het overzicht. Bij een rechtstreeks
	 * geopende detailpagina is dat resultaat er nog niet, dan halen we het op met de standaardfilters.
	 */
	private readonly positie = computed(() => this.zoek.resultaat()?.findIndex((chat) => chat.id === String(this.id())) ?? -1);

	readonly vorige = computed(() => (this.positie() > 0 ? this.zoek.resultaat()?.[this.positie() - 1] : undefined));

	readonly volgende = computed(() => {
		const resultaat = this.zoek.resultaat();
		return resultaat && this.positie() >= 0 ? resultaat[this.positie() + 1] : undefined;
	});

	constructor() {
		if (!this.zoek.resultaat()) this.zoek.aiChats$.pipe(takeUntilDestroyed()).subscribe();
	}

	readonly aiChatConversation$: Observable<RAIChatConversatie> = this.id$.pipe(
		filter((id): id is number => id !== undefined),
		switchMap((id) => this.restService.getAIChatConversatie(id)),
		shareReplay(1)
	);

	readonly conversatie = toSignal(this.aiChatConversation$);

	private readonly opgehaaldeBehandeling = toSignal(
		this.id$.pipe(
			filter((id): id is number => id !== undefined),
			switchMap((id) => this.restService.getAIChatBehandeling(String(id)))
		)
	);

	/**
	 * De behandeling komt uit een apart admin-endpoint en zit niet in de conversatie: die wordt door
	 * de user-app met een PUT van het hele object geschreven. Elke mutatie geeft de volledige
	 * behandeling terug, zodat we hier nooit zelf een lijst hoeven bij te werken.
	 *
	 * Dit is ook de bron voor de status, de behandelaar en het wijzigingstijdstip: die staan óók op de
	 * conversatie, maar die kopie is vanaf het ophalen verouderd. Schrijfbaar om de mutatie-antwoorden
	 * direct te kunnen zetten; zodra een andere conversatie wordt opgehaald vervalt dat weer.
	 */
	readonly behandeling = linkedSignal(() => this.opgehaaldeBehandeling());

	readonly conversatieOpmerkingen = computed(() => (this.behandeling()?.opmerkingen ?? []).filter((opmerking) => opmerking.turnIndex == null));

	/**
	 * Wie er aan het woord was: de impersonator als er namens de gebruiker was ingelogd, anders de
	 * gebruiker zelf. Dezelfde keuze als in het overzicht.
	 */
	readonly gebruikersnaam = computed(() => {
		const conversatie = this.conversatie();
		if (!conversatie) return undefined;

		return conversatie.impersonator ? this.formatImpersonator(conversatie.impersonator) : conversatie.account.naam;
	});

	readonly statusOptions = Object.values(AIChatConversatieStatus).map((status) => new Option(status, getAIChatConversatieStatusName(status)));

	readonly otherConversations$ = this.aiChatConversation$.pipe(
		map((conversatie) => Number(conversatie.account.id)),
		filter(Boolean),
		switchMap((accountId) => this.restService.getAIChatConversaties({ account: accountId }))
	);

	getSelectedStatus() {
		return this.statusOptions.find((option) => option.value === this.behandeling()?.status);
	}

	async setStatus(status: AIChatConversatieStatus) {
		const conversatieId = this.conversatie()?.id;
		if (!conversatieId || this.behandeling()?.status === status) return;

		// Alleen de behandelvelden gaan over de lijn: een PUT van de hele conversatie zou de turns
		// terugdraaien die de gebruiker sinds het ophalen heeft toegevoegd.
		await this.zetBehandeling(this.restService.putAIChatBehandelingStatus(conversatieId, status));
	}

	async voegOpmerkingToe(turnIndex?: number) {
		const conversatieId = this.conversatie()?.id;
		if (!conversatieId) return;

		const tekst = await this.vraagTekst({ turnIndex });
		if (!tekst) return;

		await this.zetBehandeling(this.restService.postAIChatOpmerking(conversatieId, { turnIndex, tekst }));
	}

	async wijzigOpmerking(opmerking: RAIChatOpmerking) {
		const conversatieId = this.conversatie()?.id;
		if (!conversatieId) return;

		const tekst = await this.vraagTekst({ tekst: opmerking.tekst, turnIndex: opmerking.turnIndex });
		if (!tekst || tekst === opmerking.tekst) return;

		await this.zetBehandeling(this.restService.putAIChatOpmerking(conversatieId, opmerking.id, { turnIndex: opmerking.turnIndex, tekst }));
	}

	async verwijderOpmerking(opmerking: RAIChatOpmerking) {
		const conversatieId = this.conversatie()?.id;
		if (!conversatieId) return;

		// Bevestiging omdat een notitie niet soft-deleted wordt: weg is weg.
		const bevestigd = await firstValueFrom(this.dialog.open<boolean>(this.confirmOpmerkingVerwijderen).closed);
		if (!bevestigd) return;

		await this.zetBehandeling(this.restService.deleteAIChatOpmerking(conversatieId, opmerking.id));
	}

	/**
	 * Elke mutatie geeft de volledige behandeling terug, maar tijdens het wachten kan de admin al naar
	 * een andere conversatie zijn gesprongen — het component blijft dan bestaan en alleen `id` wijzigt.
	 * Zo'n antwoord hoort niet meer bij wat er op het scherm staat en gooien we weg; de zojuist
	 * opgehaalde behandeling van de nieuwe conversatie blijft dan staan.
	 */
	private async zetBehandeling(mutatie: Observable<RAIChatBehandeling>) {
		const id = this.id();

		let behandeling: RAIChatBehandeling;
		try {
			behandeling = await firstValueFrom(mutatie);
		} catch {
			this.toastr.error('De wijziging kon niet worden opgeslagen. Probeer het opnieuw.');
			return;
		}

		if (this.id() === id) this.behandeling.set(behandeling);
	}

	private vraagTekst(data: OpmerkingDialogData): Promise<string | undefined> {
		return firstValueFrom(this.dialog.open<string>(AIChatOpmerkingDialogComponent, { data }).closed);
	}

	gotoChat(chat: RAIChatConversatie) {
		this.scrollService.scrollContentToTop();
		this.router.navigate(['ai-chat', chat.id]);
	}

	goToDashboard(dashboardURLComponents: DashboardURLComponents) {
		// Impersonate de gebruiker en redirect naar het betreffende dashboard
		const account = this.conversatie()?.account;
		if (!account) return;

		const subject = `U/${account.id}`;
		const url = dashboardURLComponents.url.join('/');
		const queryParams = { ...dashboardURLComponents.queryParams, chat: this.conversatie()?.id };
		const stringQueryParams: Record<string, string> = Object.fromEntries(
			Object.entries(queryParams)
				.filter(([_, v]) => v !== undefined && v !== null)
				.map(([k, v]) => [k, String(v)])
		);
		const redirectUrl = url + '?' + new URLSearchParams(stringQueryParams).toString();
		window.open(
			`${this.envConfig.authUrl}/impersonate?subjectToImpersonate=${encodeURIComponent(subject)}&redirectTo=${encodeURIComponent(redirectUrl)}`,
			'_blank'
		);
	}

	goBack(): void {
		this.router.navigate(['ai-chat']);
	}

	formatImpersonator(impersonator: string): string {
		return impersonator.replace(/^[AD]\//, '');
	}

	getAIChatConversatieStatusName = getAIChatConversatieStatusName;
}
